/**
 * Notepad Editor Panel
 *
 * In-drawer editor panel that replaces centered modal dialogs.
 * Renders inside the Notepad drawer for editing tasks and notes.
 * Handles unsaved changes confirmation.
 */

import React, { useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { NotepadEditorMode, NotepadEntityType, useNotepadEditor } from './useNotepadEditorState';
import { TaskEditorForm, TaskEditorFormRef } from './TaskEditorForm';
import { NoteEditorForm, NoteEditorFormRef } from './NoteEditorForm';
import { NotepadConfirmDialog } from './NotepadConfirmDialog';

interface NotepadEditorPanelProps {
  mode: NotepadEditorMode;
  entityType: NotepadEntityType;
  entityId: string | null;
  onBack: () => void;
  onSaved: () => void;
}

export interface NotepadEditorPanelRef {
  triggerSave: () => void;
  handleBack: () => void;
}

/**
 * Get the panel title based on mode and entity type
 */
function getPanelTitle(
  mode: NotepadEditorMode,
  entityType: NotepadEntityType,
  t: (key: string, fallback: string) => string
): string {
  if (entityType === 'task') {
    return mode === 'create'
      ? t('notepad:task.newTitle', 'New Task')
      : t('notepad:task.editTitle', 'Edit Task');
  }
  return mode === 'create'
    ? t('notepad:note.newTitle', 'New Note')
    : t('notepad:note.editTitle', 'Edit Note');
}

export const NotepadEditorPanel = forwardRef<NotepadEditorPanelRef, NotepadEditorPanelProps>(
  function NotepadEditorPanel({ mode, entityType, entityId, onBack, onSaved }, ref) {
    const { t } = useTranslation(['notepad', 'common']);
    const { isDirty, setDirty } = useNotepadEditor();
    const [showConfirm, setShowConfirm] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Refs to call save on forms
    const taskFormRef = useRef<TaskEditorFormRef>(null);
    const noteFormRef = useRef<NoteEditorFormRef>(null);

    const title = getPanelTitle(mode, entityType, t);

    // Handle back/cancel with dirty check
    const handleBack = useCallback(() => {
      if (isDirty) {
        setShowConfirm(true);
      } else {
        onBack();
      }
    }, [isDirty, onBack]);

    // Handle save from confirm dialog
    const handleConfirmSave = useCallback(() => {
      setIsSaving(true);
      if (entityType === 'task') {
        taskFormRef.current?.save();
      } else {
        noteFormRef.current?.save();
      }
      // The form's onSaved callback will handle closing
    }, [entityType]);

    // Handle discard from confirm dialog
    const handleConfirmDiscard = useCallback(() => {
      setShowConfirm(false);
      setDirty(false);
      onBack();
    }, [onBack, setDirty]);

    // Handle cancel from confirm dialog (stay editing)
    const handleConfirmCancel = useCallback(() => {
      setShowConfirm(false);
    }, []);

    // Wrap onSaved to reset saving state and close confirm
    const handleSaved = useCallback(() => {
      setIsSaving(false);
      setShowConfirm(false);
      onSaved();
    }, [onSaved]);

    // Handle ESC key
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          handleBack();
        }
      },
      [handleBack]
    );

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        triggerSave: handleConfirmSave,
        handleBack,
      }),
      [handleConfirmSave, handleBack]
    );

    return (
      <div className="flex flex-col h-full" onKeyDown={handleKeyDown}>
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b bg-background">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="shrink-0"
            aria-label={t('common:actions.back', 'Back')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-lg font-semibold flex-1">{title}</h2>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto">
          {entityType === 'task' ? (
            <TaskEditorForm
              ref={taskFormRef}
              mode={mode}
              taskId={entityId}
              onSaved={handleSaved}
              onCancel={handleBack}
              onDirtyChange={setDirty}
            />
          ) : (
            <NoteEditorForm
              ref={noteFormRef}
              mode={mode}
              noteId={entityId}
              onSaved={handleSaved}
              onCancel={handleBack}
              onDirtyChange={setDirty}
            />
          )}
        </div>

        {/* Unsaved Changes Confirm Dialog */}
        <NotepadConfirmDialog
          open={showConfirm}
          onOpenChange={setShowConfirm}
          onSave={handleConfirmSave}
          onDiscard={handleConfirmDiscard}
          onCancel={handleConfirmCancel}
          isSaving={isSaving}
        />
      </div>
    );
  }
);
